import {
  APIIdentifiedObjects,
  BlobDicts,
  HydratedAPIIdentifiedObject,
  HydratedBlob,
  HydratedProperties,
  HydrationStatus,
} from 'data-frames/DataFrameTypes';
import {BenchlingBlob} from 'schemas/tags/BlobConstants';
import {NO_ACCESS_CHIP_TEXT} from 'ui-library/chips/NoAccessChip';
import {JSONValue} from 'util/Types';

/**
 * Check if `value` points to a hydrated API identified object or blob.
 */
function isHydrated(
  value: JSONValue,
  hydratedPropertiesNode: BlobDicts | APIIdentifiedObjects
): value is string {
  return (
    typeof value === 'string' &&
    hydratedPropertiesNode.hasOwnProperty(value) &&
    hydratedPropertiesNode[value].status === HydrationStatus.HYDRATED
  );
}

/**
 * Check if `value` points to an inaccessible API identified object or blob.
 */
function isInaccessibleHydratedProperty(
  value: JSONValue,
  hydratedPropertiesNode: BlobDicts | APIIdentifiedObjects
): value is string {
  return (
    typeof value === 'string' &&
    hydratedPropertiesNode.hasOwnProperty(value) &&
    hydratedPropertiesNode[value].status === HydrationStatus.INACCESSIBLE
  );
}

/**
 * Returns the name of a API Identified Object if it is HYDRATED, "(No access)" if it is INACCESSIBLE, and
 * null otherwise.
 */
export function apiIdentifiedObjectToReadable(
  value: JSONValue,
  hydratedProperties: Pick<HydratedProperties, 'apiIdentifiedObjects'>
): string | null {
  return isHydrated(value, hydratedProperties.apiIdentifiedObjects)
    ? (hydratedProperties.apiIdentifiedObjects[value] as HydratedAPIIdentifiedObject).name
    : isInaccessibleHydratedProperty(value, hydratedProperties.apiIdentifiedObjects)
      ? NO_ACCESS_CHIP_TEXT
      : null;
}

/**
 * Returns a hydrated blob if it exists and is hydrated, else null.
 *
 * @param value
 * @param hydratedProperties
 */
export function getHydratedBlob(
  value: JSONValue,
  hydratedProperties: Pick<HydratedProperties, 'blobDicts'>
): BenchlingBlob | null {
  return isHydrated(value, hydratedProperties.blobDicts)
    ? {...(hydratedProperties.blobDicts[value] as HydratedBlob).blob, previewFile: null}
    : null;
}
